import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useRouter } from 'next/router';
import {
  isEmpty, debounce,
} from 'lodash';
import { Search } from '@mui/icons-material';
import { TextField, CircularProgress } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useApi } from 'lib/contexts/ApplicationState';
import { FilterResult } from 'lib/types/filters';
import {
  clearQuery, updateRouterQuery,
} from 'lib/utils/filterUtils';
import { filterOptions, formatFilterResults, MINIMUM_INPUT_LENGTH } from 'components/MultiFilters/utils';
import { DEBOUNCE_TIME } from 'lib/constants';

const defaultLabel = 'Search brands, people, companies, industries, products';
interface Props {
  filters: (FilterResult | string)[];
  label?: string;
}

export const CreatorMetadataMultiFilter = ({
  filters,
  label,
}: Props) => {
  const API = useApi();
  const router = useRouter();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<FilterResult[]>([]);
  const isFetching = useRef(false);

  const filterMetadataOptions = useMemo(
    () => debounce(async (input, callback) => {
      try {
        // If the input has not enough characters, return empty
        if ((input || '').length < MINIMUM_INPUT_LENGTH) {
          isFetching.current = false;
          callback([]);
          return null;
        }

        const searchResults = await API.getCreatorMetadataFilter(input);
        const formattedResults = formatFilterResults(searchResults, true);
        isFetching.current = false;
        callback(formattedResults);
        return null;
      } catch (error) {
        isFetching.current = false;
        callback([]);
        return null;
      }
    }, DEBOUNCE_TIME),
    [API],
  );

  useEffect(() => {
    let active = true;
    if (isEmpty(inputValue)) {
      setOptions([]);
      isFetching.current = false;
      return undefined;
    }

    isFetching.current = true;
    filterMetadataOptions(inputValue, (results: FilterResult[]) => {
      isFetching.current = false;
      if (active && results) {
        setOptions(results);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, filterMetadataOptions]);

  const renderOptionImg = () => (<Search />);

  const renderSelectedLabel = (option: FilterResult | string) => {
    if (typeof option === 'string') {
      return option;
    }

    const queryToLabel: Record<string, string> = {
      brands: 'brand',
      people: 'person',
      companies: 'company',
      industries: 'industry',
      products: 'product',
    };

    const prefix = queryToLabel[option.query] || option.query;
    return `${prefix}: ${option.label}`;
  };

  const onChange = (values: (FilterResult | string)[]) => {
    const buildMultiQuery = () => values.reduce(
      (acc, value) => {
        if (typeof value === 'string') {
          // For freeSolo string values, we don't know the type, so skip
          return acc;
        }

        const key: string = value.query || 'keyword';
        // For metadata fields, the value.id is the string value itself (not a numeric ID)
        // So we use it directly as the slug, or fall back to the label
        const slug = typeof value.id === 'string' ? value.id : (value.label || '');
        if (!acc[key]) {
          acc[key] = [slug];
        } else {
          acc[key].push(slug);
        }

        return acc;
      }, {} as any,
    );

    const query = {
      ...clearQuery(['brands', 'people', 'companies', 'industries', 'products']),
      ...buildMultiQuery(),
    };
    updateRouterQuery(router, query);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      loading={!!inputValue?.length && isFetching.current}
      value={filters}
      filterOptions={filterOptions}
      className="mx-auto"
      autoHighlight
      getOptionLabel={(option) => renderSelectedLabel(option)}
      options={[...options]}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue.trim());
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={label || defaultLabel}
          fullWidth
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <div className="absolute text-mediumgray right-4 top-3">
                {isFetching.current ? <CircularProgress size={20} /> : null}
              </div>
            ),
          }}
          onKeyDown={(e) => {
            if (e.code === 'Enter' && isFetching.current) {
              e.stopPropagation();
            }
          }}
        />
      )}
      //@ts-ignore
      renderOption={
        (props, option: FilterResult) => (
          <li {...props} key={option.id}>
            <div className="flex w-full">
              <div className="w-20">
                {renderOptionImg()}
              </div>
              <div className="w-4/5">
                <div className="font-bold">
                  {option.label}
                </div>
                <div className="text-mediumgray text-sm">
                  {option.subLabel}
                </div>
              </div>
            </div>
          </li>
        )
      }
      size="small"
    />
  );
};
